<template>
  <ScrollTop />

  <div class="project-list-page w-full max-w-7xl mx-auto">
    <div class="page-hero">
      <div>
        <h1>{{ $t('title.projects') }}</h1>
        <p>{{ $t('message.project_list_hint') }}</p>
      </div>

      <CreateProjectButton class="hero-action" />
    </div>

    <section class="stats-grid" aria-label="Projectstatistieken">
      <div
        v-for="stat in projectStats"
        :key="stat.key"
        class="stat-tile"
      >
        <div :class="['stat-icon', stat.tone]">
          <i :class="stat.icon"></i>
        </div>
        <div>
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </section>

    <section class="floating-panel controls-panel">
      <IconField class="search-field">
        <InputIcon class="fal fa-search text-zinc-500 dark:text-zinc-400" />
        <InputText
          v-model.trim="search"
          type="search"
          :placeholder="$t('placeholder.search')"
          class="w-full"
        />
      </IconField>
    </section>

    <section class="projects-table" aria-label="Projecten">
      <div class="projects-body">
        <template v-if="isLoadingProjects">
          <article
            v-for="index in 8"
            :key="`project-skeleton-${index}`"
            class="project-row project-row--skeleton"
            aria-busy="true"
          >
            <div class="project-main">
              <Skeleton class="project-skeleton-image" />
              <div class="project-identity">
                <Skeleton class="project-skeleton-reference" />
                <Skeleton class="project-skeleton-description" />
              </div>
            </div>

            <div class="table-cell project-hours">
              <div class="hours-panel hours-panel--skeleton">
                <div class="hours-summary">
                  <span v-for="metric in 3" :key="metric" class="hours-metric">
                    <Skeleton class="hours-skeleton-label" />
                    <Skeleton class="hours-skeleton-value" />
                  </span>
                </div>
                <Skeleton class="hours-skeleton-progress" />
              </div>
            </div>

            <div class="row-actions">
              <div class="action-buttons">
                <Skeleton class="row-action-skeleton" />
                <Skeleton class="row-action-skeleton" />
              </div>
            </div>
          </article>
        </template>

        <template v-else>
          <article
            v-for="project in filteredProjects"
            :key="project.reference"
            class="project-row"
          >
            <a
              class="project-open-link"
              :href="projectHref(project)"
              @click.prevent="openProject(project)"
            >
              <div class="project-main" :data-label="$t('label.project')">
                <div class="project-thumb">
                  <img :src="getProjectImage(project)" alt="" />
                </div>

                <div class="project-identity">
                  <div class="project-reference">{{ project.reference }}</div>
                  <div class="project-description">
                    {{ project.name || project.description || '-' }}
                  </div>
                </div>
              </div>

              <div class="table-cell project-hours">
                <div
                  :class="[
                    'hours-panel',
                    isProjectOverBudget(project) ? 'is-over-budget' : '',
                    remainingHoursTone(project),
                  ]"
                >
                  <div class="hours-summary">
                    <span class="hours-metric">
                      <span class="hours-metric-content">
                        <small>{{ $t('label.expected_hours') }}</small>
                        <strong>{{ formatHours(project.budgetedHours) }}u</strong>
                      </span>
                    </span>
                    <span class="hours-metric">
                      <span class="hours-metric-content">
                        <small>{{ $t('label.worked_hours') }}</small>
                        <strong>{{ formatHours(project.spentHours) }}u</strong>
                      </span>
                    </span>
                    <span :class="['hours-metric', 'remaining-hours', remainingHoursTone(project)]">
                      <span class="hours-metric-content">
                        <small>{{ $t('label.total_remaining_hours') }}</small>
                        <strong>{{ formatHours(remainingProjectHours(project)) }}u</strong>
                      </span>
                    </span>
                  </div>
                  <progress
                    class="hours-progress"
                    min="0"
                    max="100"
                    :value="projectHoursProgress(project)"
                    :aria-label="$t('label.worked_hours')"
                  ></progress>
                </div>
              </div>
            </a>

            <div class="row-actions" @keydown.stop>
              <div class="action-buttons">
                <Button
                  :label="$t('label.matrix')"
                  icon="far fa-table"
                  size="small"
                  outlined
                  class="row-action-button"
                  v-tooltip="tooltip($t('label.matrix_overview'))"
                  @click.stop="openMatrix(project)"
                />
                <Button
                  :label="$t('label.open')"
                  icon="far fa-arrow-right"
                  icon-pos="right"
                  size="small"
                  outlined
                  class="row-action-button"
                  v-tooltip="tooltip($t('label.open'))"
                  :aria-label="$t('label.open')"
                  @click.stop="openProject(project)"
                />
              </div>
            </div>
          </article>

          <div v-if="filteredProjects.length === 0" class="empty-state">
            <i class="far fa-folder-open"></i>
            <span>{{ $t('no_projects_found') }}</span>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CreateProjectButton from '../components/Buttons/CreateProjectButton.vue';
import api, { reportError } from '@/utils/api.js';

export default {
  name: 'ProjectList',

  components: {
    CreateProjectButton
  },

  data() {
    return {
      search: '',
      isLoadingProjects: false,
    }
  },

  computed: {
    ...mapGetters([
      'projects',
      'user'
    ]),

    filteredProjects() {
      const projects = Array.isArray(this.projects) ? this.projects : [];

      if (!this.search?.length) {
        return projects;
      }

      const search = this.search.toLowerCase();

      return projects.filter((project) => {
        const searchString = [
          project.reference,
          project.name,
          project.description,
          this.projectStatusLabel(project),
        ].join(' ').toLowerCase();

        return searchString.includes(search);
      });
    },

    projectStats() {
      const projects = Array.isArray(this.projects) ? this.projects : [];
      const activeProjects = projects.filter((project) => !this.isProjectClosed(project)).length;
      const closedProjects = projects.length - activeProjects;
      const latestProjectDate = projects.reduce((latest, project) => {
        const date = project.createdAt ? new Date(project.createdAt) : null;

        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
          return latest;
        }

        return !latest || date > latest ? date : latest;
      }, null);

      return [
        {
          key: 'total',
          label: this.$t('label.projects_total'),
          value: projects.length,
          icon: 'far fa-folder-open',
          tone: 'tone-slate',
        },
        {
          key: 'active',
          label: this.$t('label.active_projects'),
          value: activeProjects,
          icon: 'far fa-play-circle',
          tone: 'tone-green',
        },
        {
          key: 'closed',
          label: this.$t('label.completed_projects'),
          value: closedProjects,
          icon: 'far fa-check-circle',
          tone: 'tone-amber',
        },
        {
          key: 'latest',
          label: this.$t('label.latest_project'),
          value: latestProjectDate ? this.formatDate(latestProjectDate) : '-',
          icon: 'far fa-clock',
          tone: 'tone-blue',
        },
      ];
    },
  },

  methods: {
    ...mapActions([
      'setProjects',
      'setLoading',
      'setUser'
    ]),

    openProject(project) {
      if (!project?.reference) {
        return;
      }

      this.$router.push({ name: 'ProjectDetails', params: { reference: project.reference } });
    },

    projectHref(project) {
      if (!project?.reference) {
        return '#';
      }

      return this.$router.resolve({
        name: 'ProjectDetails',
        params: { reference: project.reference },
      }).href;
    },

    openMatrix(project) {
      if (!project?.reference) {
        return;
      }

      this.$router.push({ name: 'ProjectProgressMatrix', params: { reference: project.reference } });
    },

    isProjectClosed(project) {
      return project?.isClosed === true || Boolean(project?.closedAt);
    },

    projectStatusLabel(project) {
      return this.isProjectClosed(project) ? this.$t('label.completed') : this.$t('label.active');
    },

    getProjectImage(project) {
      if (project.image) {
        return `${import.meta.env.VITE_API_ROOT}/${project.image}`;
      }

      return new URL('@/assets/images/stock-photo.png', import.meta.url).href;
    },

    formatDate(value) {
      if (!value) {
        return '-';
      }

      const date = value instanceof Date ? value : new Date(value);

      if (Number.isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString(this.$i18n?.locale === 'en' ? 'en-US' : 'nl-NL', {
        dateStyle: 'medium',
      });
    },

    formatHours(value) {
      const numeric = Number(value || 0);
      const rounded = Math.round((numeric + Number.EPSILON) * 100) / 100;

      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    },

    projectHoursProgress(project) {
      const budgetedHours = Number(project?.budgetedHours || 0);
      const spentHours = Number(project?.spentHours || 0);

      if (budgetedHours <= 0) {
        return spentHours > 0 ? 100 : 0;
      }

      return Math.min(Math.max((spentHours / budgetedHours) * 100, 0), 100);
    },

    isProjectOverBudget(project) {
      const budgetedHours = Number(project?.budgetedHours || 0);

      return budgetedHours > 0 && Number(project?.spentHours || 0) > budgetedHours;
    },

    remainingProjectHours(project) {
      return Number(project?.budgetedHours || 0) - Number(project?.spentHours || 0);
    },

    remainingHoursPercentage(project) {
      const budgetedHours = Number(project?.budgetedHours || 0);

      if (budgetedHours <= 0) {
        return null;
      }

      return (this.remainingProjectHours(project) / budgetedHours) * 100;
    },

    remainingHoursTone(project) {
      if (this.remainingProjectHours(project) < 0) {
        return 'is-critical';
      }

      const percentage = this.remainingHoursPercentage(project);

      if (percentage === null) {
        return '';
      }

      if (percentage < 10) {
        return 'is-critical';
      }

      return percentage < 20 ? 'is-warning' : '';
    },

    tooltip(value) {
      return {
        value,
        showDelay: 800,
        hideDelay: 300,
        pt: {
          text: 'text-sm px-2.5 py-1.5'
        }
      };
    },
  },

  async created() {
    try {
      this.isLoadingProjects = this.projects.length === 0;
      const { data } = await api.get('/projects');

      this.setProjects(data.projects);
    } catch (error) {
      reportError(error);
      this.$toast.add({ severity: 'error', summary: this.$t('toast.generic_error_try_again'), life: 5000 });
    } finally {
      this.isLoadingProjects = false;
    }
  },
};
</script>

<style scoped>
.project-list-page {
  padding: 0 0 2rem;
}

.page-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1.5rem;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);
}

.dark .page-hero {
  border-color: #27272a;
  background: rgba(24, 24, 27, 0.78);
}

.page-hero h1 {
  margin: 0 0 0.35rem;
  color: #18181b;
  font-size: clamp(1.6rem, 2.8vw, 2.4rem);
  font-weight: 800;
  line-height: 1.1;
}

.dark .page-hero h1 {
  color: #fafafa;
}

.page-hero p {
  max-width: 46rem;
  color: #52525b;
  font-size: 0.95rem;
}

.dark .page-hero p {
  color: #d4d4d8;
}

.hero-action {
  flex: 0 0 auto;
  border-radius: 999px;
}

.floating-panel,
.stat-tile {
  border: 1px solid #e4e4e7;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(18px);
}

.dark .floating-panel,
.dark .stat-tile {
  border-color: #27272a;
  background: rgba(24, 24, 27, 0.74);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.8rem;
  margin-bottom: 0.9rem;
}

.stat-tile {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-height: 5.4rem;
  padding: 1.25rem;
  border-radius: 16px;
}

.stat-icon {
  display: grid;
  flex: 0 0 auto;
  width: 2.7rem;
  height: 2.7rem;
  place-items: center;
  border-radius: 13px;
  font-size: 1rem;
}

.tone-slate {
  color: #334155;
  background: #e2e8f0;
}

.tone-green {
  color: #166534;
  background: #dcfce7;
}

.tone-amber {
  color: #92400e;
  background: #fef3c7;
}

.tone-blue {
  color: #1d4ed8;
  background: #dbeafe;
}

.dark .tone-slate {
  color: #cbd5e1;
  background: rgba(71, 85, 105, 0.34);
}

.dark .tone-green {
  color: #86efac;
  background: rgba(22, 101, 52, 0.34);
}

.dark .tone-amber {
  color: #fcd34d;
  background: rgba(146, 64, 14, 0.34);
}

.dark .tone-blue {
  color: #93c5fd;
  background: rgba(29, 78, 216, 0.34);
}

.stat-value {
  overflow: hidden;
  color: #18181b;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .stat-value {
  color: #fafafa;
}

.stat-label {
  margin-top: 0.25rem;
  color: #71717a;
  font-size: 0.8rem;
  font-weight: 650;
}

.dark .stat-label {
  color: #a1a1aa;
}

.controls-panel {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.9rem;
  padding: 1.25rem;
  border-radius: 16px;
}

.search-field {
  flex: 1 1 auto;
  min-width: 14rem;
}

.projects-table {
  overflow: hidden;
  border: 1px solid #e4e4e7;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(18px);
}

.dark .projects-table {
  border-color: #27272a;
  background: rgba(24, 24, 27, 0.72);
}

.project-row {
  display: grid;
  grid-template-columns:
    minmax(23rem, 1fr)
    minmax(26rem, 30rem)
    minmax(13rem, 1fr);
  align-items: center;
  min-height: 7.6rem;
  padding: 0 1.25rem;
  border: 0;
  border-bottom: 1px solid #e4e4e7;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.projects-body {
  display: flex;
  flex-direction: column;
}

.project-row:last-child {
  border-bottom: 0;
}

.project-row:hover,
.project-row:has(.project-open-link:focus-visible) {
  background: rgba(248, 250, 252, 0.72);
  outline: none;
}

.dark .project-row {
  border-bottom-color: #27272a;
}

.dark .project-row:hover,
.dark .project-row:has(.project-open-link:focus-visible) {
  background: rgba(39, 39, 42, 0.64);
}

.project-open-link {
  display: contents;
  color: inherit;
  text-decoration: none;
}

.project-row:has(.row-action-button:hover),
.project-row:has(.row-action-button:focus-visible),
.dark .project-row:has(.row-action-button:hover),
.dark .project-row:has(.row-action-button:focus-visible) {
  background: transparent;
}

.project-main {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.9rem;
}

.project-thumb {
  flex: 0 0 auto;
  position: relative;
  overflow: hidden;
  width: 8.75rem;
  height: 5.5rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 12px;
  background: #f4f4f5;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.dark .project-thumb {
  border-color: rgba(63, 63, 70, 0.72);
  background: #27272a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
}

.project-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.92) contrast(0.96);
  transform: translateZ(0);
}

.project-thumb::after {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(15, 23, 42, 0.08));
  content: '';
  pointer-events: none;
}

.project-skeleton-image {
  flex: 0 0 auto;
  overflow: hidden;
  width: 8.75rem;
  height: 5.5rem;
  border-radius: 12px;
}

.project-skeleton-reference {
  width: min(9rem, 100%);
  height: 1.1rem;
}

.project-skeleton-description {
  width: min(16rem, 100%);
  height: 0.85rem;
  margin-top: 0.5rem;
}

.project-identity {
  min-width: 0;
}

.project-reference {
  overflow: hidden;
  color: #18181b;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .project-reference {
  color: #fafafa;
}

.project-description {
  overflow: hidden;
  margin-top: 0.2rem;
  color: #71717a;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .project-description {
  color: #a1a1aa;
}

.table-cell,
.row-actions {
  overflow: hidden;
  min-width: 0;
  color: #27272a;
  font-size: 0.9rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .table-cell,
.dark .row-actions {
  color: #f4f4f5;
}

.project-hours {
  display: flex;
  justify-content: center;
  padding: 0 1rem;
}

.hours-panel {
  width: 100%;
  max-width: 30rem;
}

.hours-panel--skeleton {
  min-width: 0;
}

.hours-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0.65rem 0.8rem 0.55rem;
}

.hours-metric {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 1rem;
}

.hours-metric + .hours-metric {
  padding-left: 1rem;
  border-left: 1px solid rgba(148, 163, 184, 0.22);
}

.dark .hours-metric + .hours-metric {
  border-left-color: rgba(82, 82, 91, 0.78);
}

.hours-metric-content {
  display: flex;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
}

.hours-metric-content small {
  max-width: 100%;
  text-align: left;
}

.hours-metric-content strong {
  align-self: flex-start;
  text-align: left;
}

.hours-metric small {
  overflow: hidden;
  color: #71717a;
  font-size: 0.66rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hours-metric strong {
  overflow: hidden;
  color: #18181b;
  font-size: 0.9rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hours-skeleton-label {
  width: min(4.75rem, 100%);
  height: 0.66rem;
}

.hours-skeleton-value {
  width: min(3.5rem, 100%);
  height: 0.9rem;
  margin-top: 0.35rem;
}

.hours-skeleton-progress {
  display: block;
  width: calc(100% - 1.6rem);
  height: 0.25rem;
  margin: 0 0.8rem;
  border-radius: 999px;
}

.dark .hours-metric small {
  color: #a1a1aa;
}

.dark .hours-metric strong {
  color: #fafafa;
}

.remaining-hours.is-warning strong {
  color: #b45309;
}

.remaining-hours.is-critical strong {
  color: #dc2626;
}

.dark .remaining-hours.is-warning strong {
  color: #fbbf24;
}

.dark .remaining-hours.is-critical strong {
  color: #f87171;
}

.hours-progress {
  display: block;
  overflow: hidden;
  width: calc(100% - 1.6rem);
  height: 0.25rem;
  margin: 0 0.8rem;
  border: 0;
  border-radius: 999px;
  appearance: none;
  background: #e4e4e7;
}

.hours-progress::-webkit-progress-bar {
  background: #e4e4e7;
}

.dark .hours-progress,
.dark .hours-progress::-webkit-progress-bar {
  background: #3f3f46;
}

.hours-progress::-webkit-progress-value {
  border-radius: inherit;
  background: #0d9488;
}

.hours-progress::-moz-progress-bar {
  border-radius: inherit;
  background: #0d9488;
}

.hours-panel.is-over-budget .hours-progress::-webkit-progress-value,
.hours-panel.is-warning .hours-progress::-webkit-progress-value,
.hours-panel.is-over-budget .hours-progress::-moz-progress-bar,
.hours-panel.is-warning .hours-progress::-moz-progress-bar {
  background: #d97706;
}

.hours-panel.is-critical .hours-progress::-webkit-progress-value,
.hours-panel.is-critical .hours-progress::-moz-progress-bar {
  background: #dc2626;
}

.row-actions {
  display: flex;
  justify-content: flex-end;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  max-width: 13rem;
}

.row-action-button {
  flex: 1 1 50%;
  justify-content: center;
  min-width: 0;
  height: 2.4rem;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 750;
  white-space: nowrap;
}

.row-action-skeleton {
  flex: 1 1 50%;
  min-width: 0;
  height: 2.4rem;
  border-radius: 8px;
}

.row-action-button:hover,
.row-action-button:focus-visible {
  border-color: #0284c7;
  background: rgba(14, 165, 233, 0.12);
  color: #0369a1;
}

.dark .row-action-button:hover,
.dark .row-action-button:focus-visible {
  border-color: #38bdf8;
  background: rgba(14, 165, 233, 0.16);
  color: #bae6fd;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  min-height: 8rem;
  color: #71717a;
}

.dark .empty-state {
  color: #a1a1aa;
}

@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .project-row {
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
    min-height: 0;
    padding: 1.25rem;
  }

  .project-main {
    grid-column: 1 / -1;
  }

  .project-row--skeleton .project-main {
    grid-column: 1 / -1;
  }

  .table-cell,
  .row-actions {
    white-space: normal;
  }

  .row-actions {
    justify-content: flex-end;
  }

  .project-hours {
    padding: 0;
  }

  .action-buttons {
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .page-hero,
  .controls-panel {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-action {
    width: 100%;
  }

  .stats-grid,
  .project-row {
    grid-template-columns: 1fr;
  }

  .project-thumb {
    width: 7.25rem;
    height: 4.6rem;
  }

  .project-skeleton-image {
    width: 7.25rem;
    height: 4.6rem;
  }

  .project-hours,
  .row-actions,
  .hours-panel,
  .action-buttons {
    width: 100%;
    max-width: none;
  }

  .hours-summary {
    padding-right: 0.2rem;
    padding-left: 0.2rem;
  }

  .hours-metric {
    padding-right: 0.65rem;
    padding-left: 0.65rem;
  }

  .hours-metric + .hours-metric {
    padding-left: 0.65rem;
  }

  .row-actions,
  .action-buttons {
    justify-content: stretch;
  }
}
</style>
